import { MANUALLY_CONFIRMABLE_STATUSES, type SafetyVideoStatus } from "@unbox/api";
import { Check, ShieldPlus } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

type ReviewableStatus = (typeof MANUALLY_CONFIRMABLE_STATUSES)[number];

const COPY: Record<ReviewableStatus, { body: string; question: string; confirm: string; flag: string }> = {
  noRisksFound: {
    body: "No risks were identified in this video.",
    question: "Agree with our analysis?",
    confirm: "Yes, mark as reviewed",
    flag: "No, add a risk",
  },
  unableToAnalyse: {
    body: "We could not analyse this video, so nothing has been checked. Review it yourself to confirm if a risk is present.",
    question: "Manual analyse of video",
    confirm: "No risks found",
    flag: "Risk found",
  },
  videoPrivated: {
    body: "This video is private, so we couldn't analyse it. Review it yourself to confirm if a risk is present.",
    question: "Manual analyse of video",
    confirm: "No risks found",
    flag: "Risk found",
  },
};

export const isReviewableStatus = (status: SafetyVideoStatus | undefined): status is ReviewableStatus =>
  (MANUALLY_CONFIRMABLE_STATUSES as readonly string[]).includes(status ?? "");

/**
 * Radix, not react-aria: the video drawer is a Radix modal that blocks pointer events outside it,
 * so a react-aria popover there can't be hovered or clicked.
 */
export const SafetyStatusReviewPopover = ({
  status,
  badge,
  onConfirm,
  onFlag,
}: {
  status: ReviewableStatus;
  badge: ReactNode;
  onConfirm: () => void;
  onFlag: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const copy = COPY[status];

  const choose = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={0} closeDelay={120}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="flex cursor-pointer rounded-full outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {badge}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        role="dialog"
        aria-label={copy.question}
        align="start"
        sideOffset={8}
        className="flex w-[304px] flex-col gap-3 rounded-xl border border-secondary bg-primary p-4 text-primary shadow-lg"
      >
        <p className="text-sm text-tertiary">{copy.body}</p>
        <div className="flex flex-col gap-2 border-t border-secondary pt-3">
          <p className="text-sm font-medium text-primary">{copy.question}</p>
          <button
            type="button"
            onClick={() => choose(onConfirm)}
            className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-secondary px-3 py-2.5 text-left text-sm font-semibold text-primary transition-colors duration-100 hover:border-utility-success-300 hover:bg-utility-success-50"
          >
            <Check className="size-[18px] shrink-0 text-fg-success-primary" />
            {copy.confirm}
          </button>
          <button
            type="button"
            onClick={() => choose(onFlag)}
            className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-secondary px-3 py-2.5 text-left text-sm font-semibold text-primary transition-colors duration-100 hover:border-utility-error-300 hover:bg-utility-error-50"
          >
            <ShieldPlus className="size-[18px] shrink-0 text-fg-error-primary" />
            {copy.flag}
          </button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
